import { registerAuditLabels, type StatusMap } from "@vixio/ui";

export { formatUsd } from "@vixio/ai_gateway/cost";

/** Micro-dólares ↔ dólares na tela. */
export const toUsd = (micros: number | null | undefined) => (micros ?? 0) / 1_000_000;
export const fromUsd = (usd: number | null | undefined) => Math.round((usd ?? 0) * 1_000_000);

export const USAGE_STATUS: StatusMap = {
  ok: { label: "ok", tone: "ok" },
  error: { label: "erro", tone: "crit" },
  blocked: { label: "bloqueado", tone: "warn" },
  guardrail: { label: "retido", tone: "warn" },
};

export const RUN_STATUS: StatusMap = {
  queued: { label: "na fila", tone: "neutral" },
  running: { label: "executando", tone: "info" },
  done: { label: "concluída", tone: "ok" },
  held: { label: "retida", tone: "warn" },
  blocked: { label: "bloqueada", tone: "warn" },
  failed: { label: "erro", tone: "crit" },
  canceled: { label: "cancelada", tone: "neutral" },
};

export const FILE_STATUS: StatusMap = {
  queued: { label: "na fila", tone: "neutral" },
  extracting: { label: "extraindo", tone: "info" },
  indexed: { label: "indexado", tone: "ok" },
  failed: { label: "erro", tone: "crit" },
};

export const GUARD_STAGES: Record<string, string> = {
  output: "Saída (resposta do modelo)",
  input: "Entrada (pedido, antes do modelo)",
};

export const GUARD_KINDS: Record<string, string> = {
  instruction: "Instrução no prompt",
  block_pattern: "Bloqueia a resposta se aparecer",
  require_pattern: "Exige na resposta",
};

export interface Settings {
  org_id: string;
  monthly_budget_micros: number;
  block_on_limit: boolean;
  alert_thresholds: number[];
  alert_email: string | null;
  timezone: string;
}

export interface Model {
  id: string;
  org_id: string;
  model_id: string;
  display_name: string;
  kind: "chat" | "embedding";
  context_window: number | null;
  input_micros_per_mtok: number | null;
  output_micros_per_mtok: number | null;
  supports_tools: boolean;
  active: boolean;
  notes: string | null;
}

export interface Purpose {
  id: string;
  org_id: string;
  key: string;
  label: string;
  description: string | null;
  model_id: string;
  fallback_models: string[];
  prompt_key: string | null;
  temperature: number | null;
  max_output_tokens: number | null;
  monthly_budget_micros: number;
  block_on_limit: boolean;
  guardrails: string[];
  active: boolean;
  use_knowledge: boolean;
  knowledge_limit: number;
  output_format: "text" | "json";
  required_inputs: string[];
  max_input_chars: number;
  request_role: "member" | "manager" | "admin";
}

export interface Guardrail {
  id: string;
  org_id: string;
  key: string;
  label: string;
  description: string | null;
  kind: "instruction" | "block_pattern" | "require_pattern";
  instruction: string | null;
  pattern: string | null;
  first_contact_only: boolean;
  message: string | null;
  active: boolean;
  stage: "input" | "output";
}

export interface BudgetRow {
  org_id: string;
  scope: "purpose" | "org";
  purpose_key: string | null;
  label: string;
  model_id: string | null;
  month: string;
  budget_micros: number;
  spent_micros: number;
  calls: number;
  pct: number | null;
  block_on_limit: boolean;
  blocked: boolean;
  active: boolean;
}

export interface UsageDaily {
  org_id: string;
  day: string;
  purpose_key: string;
  calls: number;
  problems: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_micros: number;
}

export interface UsageMonthly {
  org_id: string;
  month: string;
  purpose_key: string;
  model_id: string | null;
  source: string;
  calls: number;
  ok: number;
  errors: number;
  blocked: number;
  guardrail: number;
  fallbacks: number;
  unknown_price: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_micros: number;
  avg_latency_ms: number | null;
  p95_latency_ms: number | null;
}

export interface UsageRow {
  id: string;
  org_id: string;
  purpose_key: string;
  model_id: string | null;
  requested_model: string | null;
  fallback_used: boolean;
  prompt_tokens: number;
  completion_tokens: number;
  cost_micros: number;
  price_known: boolean;
  latency_ms: number | null;
  status: string;
  error: string | null;
  guardrail_key: string | null;
  origin_type: string | null;
  source: string;
  created_at: string;
}

export interface BudgetAlert {
  id: string;
  scope: "org" | "purpose";
  purpose_key: string | null;
  month: string;
  threshold: number;
  spent_micros: number;
  budget_micros: number;
  created_at: string;
}

export interface PromptView {
  id: string;
  org_id: string;
  key: string;
  title: string;
  description: string | null;
  current_version_id: string | null;
  version: number | null;
  body: string | null;
  variables: string[] | null;
  published_at: string | null;
  versions: number;
  used_by: string[] | null;
}

export interface PromptVersion {
  id: string;
  prompt_id: string;
  version: number;
  body: string;
  notes: string | null;
  created_at: string;
}

export interface KbSource {
  id: string;
  org_id: string;
  title: string;
  kind: string;
  source_ref: string | null;
  purposes: string[];
  status: "ativo" | "arquivado";
  chars: number;
  chunks: number;
  embedded: number;
  created_at: string;
  updated_at: string;
}

export interface KbHit {
  chunk_id: string;
  source_id: string;
  title: string;
  content: string;
  score: number;
  method: string;
}

export interface Citation {
  n: number;
  source_id: string;
  chunk_id: string;
  title: string;
  excerpt: string;
  score: number;
  method: string;
}

export type RunStatus = "queued" | "running" | "done" | "held" | "blocked" | "failed" | "canceled";

/** Resultado de uma execução (ai_gateway.get_run). */
export interface AiRun {
  id: string;
  org_id: string;
  purpose: string;
  purpose_label: string | null;
  status: RunStatus;
  output_text: string | null;
  output_json: Record<string, unknown> | unknown[] | null;
  citations: Citation[];
  violations: Array<{ key: string; label: string; message: string | null }>;
  model: string | null;
  cost_micros: number | null;
  latency_ms: number | null;
  attempts: number;
  max_attempts: number;
  error: string | null;
  origin_type: string | null;
  origin_id: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  terminal: boolean;
}

export interface RunRow {
  id: string;
  org_id: string;
  purpose_key: string;
  purpose_label: string | null;
  status: RunStatus;
  input_preview: string | null;
  origin_type: string | null;
  origin_id: string | null;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  model_id: string | null;
  cost_micros: number | null;
  latency_ms: number | null;
  error: string | null;
  citations: number;
  violations: number;
  requested_by: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export interface KbFile {
  id: string;
  org_id: string;
  title: string;
  file_name: string;
  extension: string;
  mime_type: string | null;
  size_bytes: number;
  purposes: string[];
  status: "queued" | "extracting" | "indexed" | "failed";
  extractor: "docling" | "plain" | null;
  chars: number | null;
  chunks: number | null;
  truncated: boolean;
  source_id: string | null;
  source_status: string | null;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  error: string | null;
  indexed_at: string | null;
  created_at: string;
  embedded: number;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "ai_gateway.set_settings": { args: { p_org: U; p_monthly_budget_micros: number; p_block_on_limit?: boolean; p_alert_thresholds?: number[]; p_alert_email?: string | null }; returns: null };
    "ai_gateway.upsert_model": {
      args: { p_org: U; p_model_id: string; p_display_name: string; p_kind?: string; p_input_micros_per_mtok?: number | null; p_output_micros_per_mtok?: number | null; p_context_window?: number | null; p_supports_tools?: boolean; p_active?: boolean; p_notes?: string | null };
      returns: U;
    };
    "ai_gateway.upsert_purpose": {
      args: {
        p_org: U; p_key: string; p_label: string; p_model_id: string; p_fallback_models?: string[]; p_prompt_key?: string | null; p_temperature?: number | null;
        p_max_output_tokens?: number | null; p_monthly_budget_micros?: number; p_block_on_limit?: boolean; p_guardrails?: string[]; p_active?: boolean; p_description?: string | null;
      };
      returns: U;
    };
    "ai_gateway.upsert_guardrail": {
      args: { p_org: U; p_key: string; p_label: string; p_kind: string; p_instruction?: string | null; p_pattern?: string | null; p_message?: string | null; p_first_contact_only?: boolean; p_active?: boolean; p_description?: string | null };
      returns: U;
    };
    "ai_gateway.upsert_prompt": { args: { p_org: U; p_key: string; p_title: string; p_description?: string | null }; returns: U };
    "ai_gateway.publish_prompt_version": { args: { p_prompt: U; p_body: string; p_notes?: string | null }; returns: U };
    "ai_gateway.kb_add_source": { args: { p_org: U; p_title: string; p_content: string; p_kind?: string; p_source_ref?: string | null; p_purposes?: string[] }; returns: U };
    "ai_gateway.kb_update_source": { args: { p_source: U; p_title: string; p_content?: string | null; p_purposes?: string[] | null }; returns: number };
    "ai_gateway.kb_set_status": { args: { p_source: U; p_status: string }; returns: null };
    "ai_gateway.kb_search": { args: { p_org: U; p_query: string; p_limit?: number; p_purpose?: string | null }; returns: KbHit[] };
    "ai_gateway.install_defaults": { args: { p_org: U }; returns: null };
    "ai_gateway.check_input": { args: { p_org: U; p_purpose: string; p_text: string }; returns: Array<{ key: string; label: string; message: string | null }> };
    "ai_gateway.set_purpose_runtime": {
      args: {
        p_org: U; p_key: string; p_use_knowledge?: boolean | null; p_knowledge_limit?: number | null; p_output_format?: string | null;
        p_required_inputs?: string[] | null; p_max_input_chars?: number | null; p_request_role?: string | null;
      };
      returns: null;
    };
    "ai_gateway.set_guardrail_stage": { args: { p_org: U; p_key: string; p_stage: "input" | "output" }; returns: null };
    "ai_gateway.request_run": {
      args: { p_org: U; p_purpose: string; p_text?: string | null; p_vars?: Record<string, unknown>; p_origin_type?: string | null; p_origin_id?: U | null; p_idempotency_key?: string | null };
      returns: { run_id: U; status: RunStatus; idempotent: boolean; error?: string | null; violations?: AiRun["violations"] };
    };
    "ai_gateway.get_run": { args: { p_run: U }; returns: AiRun };
    "ai_gateway.cancel_run": { args: { p_run: U }; returns: string };
    "ai_gateway.retry_run": { args: { p_run: U }; returns: string };
    "ai_gateway.kb_register_file": {
      args: { p_org: U; p_title: string; p_storage_path: string; p_file_name: string; p_size_bytes: number; p_mime_type?: string | null; p_purposes?: string[] };
      returns: U;
    };
    "ai_gateway.kb_retry_file": { args: { p_file: U }; returns: null };
    "ai_gateway.check_output": { args: { p_org: U; p_purpose: string; p_text: string; p_first_contact?: boolean }; returns: Array<{ key: string; label: string; message: string | null }> };
  }
}

registerAuditLabels({
  "ai_gateway.settings_set": "Orçamento de IA alterado",
  "ai_gateway.model_upserted": "Modelo de IA salvo",
  "ai_gateway.purpose_upserted": "Finalidade de IA salva",
  "ai_gateway.guardrail_upserted": "Guardrail salvo",
  "ai_gateway.prompt_upserted": "Prompt salvo",
  "ai_gateway.prompt_published": "Versão de prompt publicada",
  "ai_gateway.kb_source_added": "Fonte adicionada à base de conhecimento",
  "ai_gateway.kb_source_updated": "Fonte da base de conhecimento atualizada",
  "ai_gateway.kb_source_status": "Fonte da base arquivada ou reativada",
  "ai_gateway.defaults_installed": "Padrões da central de IA instalados",
  "ai_gateway.purpose_runtime_set": "Execução da finalidade de IA configurada",
  "ai_gateway.guardrail_stage_set": "Etapa do guardrail alterada",
  "ai_gateway.run_requested": "IA acionada",
  "ai_gateway.run_canceled": "Execução de IA cancelada",
  "ai_gateway.run_retried": "Execução de IA reprocessada",
  "ai_gateway.run_failed": "Execução de IA com erro",
  "ai_gateway.kb_file_registered": "Arquivo enviado à base de conhecimento",
  "ai_gateway.kb_file_indexed": "Arquivo indexado na base de conhecimento",
  "ai_gateway.kb_file_failed": "Arquivo da base com erro",
  "ai_gateway.kb_file_retried": "Arquivo da base reprocessado",
});
